import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { animate, stagger } from 'animejs';
import './ServiciosPage.css';
import Header from './Header';

const HERO_BG = '/images/servicios-hero-javiland.png?v=2';
const HERO_BG_FALLBACK = '/images/hero-viajes-colombia.png';

function WordSpans({ text }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <span className="servicios-word">{word}</span>
          {i < words.length - 1 && ' '}
        </React.Fragment>
      ))}
    </>
  );
}

const ServiciosPage = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const services = useMemo(() => ([
    {
      path: '/bienestar',
      icon: 'spa',
      labelKey: 'serviciosHub.wellnessTitle',
      titleKey: 'serviciosHub.wellnessTitle',
      descKey: 'serviciosHub.wellnessDesc',
      ctaKey: 'serviciosHub.wellnessCta',
      image: '/images/bienestar-carousel-yoga-meditacion.png',
      tone: 'forest',
    },
    {
      path: '/conoce-colombia',
      icon: 'map-marked-alt',
      labelKey: 'serviciosHub.travelTitle',
      titleKey: 'serviciosHub.travelTitle',
      descKey: 'serviciosHub.travelDesc',
      ctaKey: 'serviciosHub.travelCta',
      image: '/images/viajes-pacifico-ballenas.png',
      tone: 'earth',
    },
    {
      path: '/growth-partner',
      icon: 'rocket',
      labelKey: 'serviciosHub.growthTitle',
      titleKey: 'serviciosHub.growthTitle',
      descKey: 'serviciosHub.growthDesc',
      ctaKey: 'serviciosHub.growthCta',
      image: '/images/hero-carousel-marca-ecosistema.png',
      tone: 'wine',
    },
  ]), []);

  /* Animación de entrada del hero */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const kicker = hero.querySelector('.servicios-cinema-kicker');
    const words = hero.querySelectorAll('.servicios-word');
    const lead = hero.querySelector('.servicios-cinema-lead');
    const pills = hero.querySelectorAll('.servicios-hero-pill');
    const scrollCue = hero.querySelector('.servicios-scroll-cue');

    if (kicker) {
      kicker.style.opacity = '0';
      kicker.style.transform = 'translateY(16px)';
    }
    words.forEach((w) => {
      w.style.opacity = '0';
      w.style.transform = 'translateY(28px)';
    });
    if (lead) {
      lead.style.opacity = '0';
      lead.style.transform = 'translateY(20px)';
    }
    pills.forEach((p) => {
      p.style.opacity = '0';
      p.style.transform = 'translateY(14px)';
    });
    if (scrollCue) {
      scrollCue.style.opacity = '0';
    }

    const run = () => {
      if (kicker) {
        animate(kicker, {
          opacity: [0, 1],
          translateY: [16, 0],
          duration: 600,
          ease: 'outExpo',
        });
      }
      if (words.length) {
        animate(words, {
          opacity: [0, 1],
          translateY: [28, 0],
          delay: stagger(70, { start: 180 }),
          duration: 720,
          ease: 'outExpo',
        });
      }
      if (lead) {
        animate(lead, {
          opacity: [0, 1],
          translateY: [20, 0],
          delay: 520,
          duration: 650,
          ease: 'outExpo',
        });
      }
      if (pills.length) {
        animate(pills, {
          opacity: [0, 1],
          translateY: [14, 0],
          delay: stagger(55, { start: 680 }),
          duration: 550,
          ease: 'outExpo',
        });
      }
      if (scrollCue) {
        animate(scrollCue, {
          opacity: [0, 1],
          delay: 1100,
          duration: 500,
          ease: 'outQuad',
        });
      }
    };

    const id = requestAnimationFrame(run);
    return () => cancelAnimationFrame(id);
  }, [t]);

  /* Reveal de tarjetas al scroll */
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const cards = grid.querySelectorAll('.servicios-showcase-card');
    cards.forEach((card) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(48px)';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animate(cards, {
            opacity: [0, 1],
            translateY: [48, 0],
            delay: stagger(100),
            duration: 680,
            ease: 'outExpo',
          });
          observer.disconnect();
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  const scrollToServices = () => {
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="servicios-page">
      <Header />

      <section className="servicios-cinema-hero" ref={heroRef}>
        <div className="servicios-cinema-bg" aria-hidden="true">
          <img
            src={HERO_BG}
            alt=""
            className="servicios-cinema-bg-img"
            onError={(e) => {
              const src = e.currentTarget.src;
              if (src.includes('hero-viajes-colombia')) return;
              e.currentTarget.src = HERO_BG_FALLBACK;
            }}
          />
        </div>
        <div className="servicios-cinema-overlay" aria-hidden="true" />
        <div className="servicios-cinema-rays" aria-hidden="true" />

        <div className="servicios-container servicios-cinema-inner">
          <span className="servicios-cinema-kicker">{t('serviciosHub.kicker')}</span>
          <h1 className="servicios-cinema-title">
            <WordSpans text={t('serviciosHub.heroTitle')} />
          </h1>
          <p className="servicios-cinema-lead">{t('serviciosHub.heroLead')}</p>

          <nav className="servicios-hero-quicknav" aria-label={t('serviciosHub.gridTitle')}>
            {services.map((svc) => (
              <button
                key={svc.path}
                type="button"
                className="servicios-hero-pill"
                onClick={() => navigateToPage(svc.path)}
              >
                <i className={`fas fa-${svc.icon}`} aria-hidden="true" />
                <span>{t(svc.labelKey)}</span>
              </button>
            ))}
          </nav>
        </div>

        <button
          type="button"
          className="servicios-scroll-cue"
          onClick={scrollToServices}
          aria-label={t('serviciosHub.scrollHint')}
        >
          <span>{t('serviciosHub.scrollHint')}</span>
          <i className="fas fa-chevron-down" aria-hidden="true" />
        </button>
      </section>

      <section className="servicios-showcase" ref={gridRef} id="servicios-listado">
        <div className="servicios-container">
          <header className="servicios-showcase-header">
            <h2 className="servicios-showcase-title">{t('serviciosHub.gridTitle')}</h2>
            <p className="servicios-showcase-sub">{t('serviciosHub.gridSub')}</p>
          </header>

          <div className="servicios-showcase-grid servicios-showcase-grid--three">
            {services.map((svc) => (
              <article
                key={svc.path}
                className={`servicios-showcase-card servicios-showcase-card--${svc.tone}`}
                onClick={() => navigateToPage(svc.path)}
              >
                <div className="servicios-showcase-card-media" aria-hidden="true">
                  <img src={svc.image} alt="" loading="lazy" />
                </div>
                <div className="servicios-showcase-card-body">
                  <div className="servicios-showcase-card-icon">
                    <i className={`fas fa-${svc.icon}`} aria-hidden="true" />
                  </div>
                  <h3 className="servicios-showcase-card-heading">{t(svc.titleKey)}</h3>
                  <p className="servicios-showcase-card-text">{t(svc.descKey)}</p>
                  <button
                    type="button"
                    className="servicios-showcase-card-cta"
                    onClick={(e) => { e.stopPropagation(); navigateToPage(svc.path); }}
                  >
                    {t(svc.ctaKey)}
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiciosPage;
